import { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Eye, EyeOff, ArrowRight, Loader2, ShoppingCart, Package, Shield, Info, CheckCircle2, Building2, Globe, Zap } from "lucide-react";
import { Separator } from "@/components/ui/separator";
import { getFriendlyError } from "@/lib/errorMessages";
import { GoogleOAuthButton, AppleOAuthButton } from "@/components/auth/OAuthButtons";
import { PasswordStrengthIndicator } from "@/components/auth/PasswordStrengthIndicator";
import { z } from "zod";
import { logger } from "@/lib/logger";
import { useAuth } from "@/hooks/useAuth";
import { USER_ROLES, getDefaultRoute, getProfileRoute } from "@/config/roles";
import { Logo } from "@/components/ui/logo";
import { MARKETING_URL } from "@/lib/urls";
import { trackSignUp, trackLogin, trackSignupAttempt } from "@/lib/analytics";

// Feature flag: Set to false to disable signups and show waitlist instead
const SIGNUPS_ENABLED = import.meta.env.VITE_SIGNUPS_ENABLED !== 'false';

// Validation schemas
const emailSchema = z.string().email("Please enter a valid email address");
const passwordSchema = z.string()
  .min(8, "Password must be at least 8 characters")
  .regex(/[A-Z]/, "Password must contain at least one uppercase letter")
  .regex(/[a-z]/, "Password must contain at least one lowercase letter")
  .regex(/\d/, "Password must contain at least one number");

// Marketing content for the left panel
const valueProps = [
  {
    icon: Building2,
    title: "Connect with verified suppliers",
    description: "Access our curated network of packaging manufacturers and distributors"
  },
  {
    icon: Zap,
    title: "Streamlined RFQ process",
    description: "Get competitive quotes faster with our intelligent matching system"
  },
  {
    icon: Globe,
    title: "Scale your business",
    description: "Whether you're sourcing or supplying, grow with HoleScale"
  }
];

const Auth = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, loading: authLoading, roles, hasRole } = useAuth();
  const [mode, setMode] = useState<"login" | "signup" | "reset">(SIGNUPS_ENABLED ? "signup" : "login");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const hasRedirected = useRef(false);
  const rolesWaitTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phone, setPhone] = useState("");
  const [role, setRole] = useState<"buyer" | "supplier">(USER_ROLES.BUYER as "buyer" | "supplier");

  // Validation errors
  const [emailError, setEmailError] = useState("");
  const [passwordError, setPasswordError] = useState("");

  // Redirect to waitlist if signups are disabled and user is trying to sign up
  useEffect(() => {
    if (!SIGNUPS_ENABLED && mode === "signup" && !user) {
      const inviteCode = searchParams.get("invite");
      // Only redirect if no invite code (invite codes bypass waitlist)
      if (!inviteCode) {
        window.location.href = `${MARKETING_URL}/waitlist`;
        return;
      }
    }
  }, [mode, user, searchParams]);

  // Pre-select role from URL parameter
  useEffect(() => {
    const typeParam = searchParams.get("type");
    if (typeParam === "buyer" || typeParam === "supplier") {
      setRole(typeParam);
      if (mode === "login") {
        setMode("signup");
      }
    }

    // Check for mode parameter
    const modeParam = searchParams.get("mode");
    if (modeParam === "signup" && SIGNUPS_ENABLED) {
      setMode("signup");
    }
  }, [searchParams, mode]);

  // Check if user is already authenticated and redirect
  useEffect(() => {
    if (authLoading) return;

    if (hasRedirected.current || !user) {
      if (!user) {
        hasRedirected.current = false;
      }
      return;
    }

    // IMPORTANT: Avoid redirect loops.
    // If roles are temporarily empty after sign-in, DO NOT redirect back to /auth.
    // That creates a self-loop and makes the app feel "broken" even when roles eventually load.
    if (user && roles.length === 0 && !hasRole(USER_ROLES.ADMIN)) {
      if (rolesWaitTimeoutRef.current) clearTimeout(rolesWaitTimeoutRef.current);

      rolesWaitTimeoutRef.current = setTimeout(() => {
        // Just log; ProtectedRoute will continue to show a stable loading/fallback UI.
        logger.warn('Roles still empty after timeout on /auth (no redirect)', { userId: user.id });
      }, 7000);

      return () => {
        if (rolesWaitTimeoutRef.current) clearTimeout(rolesWaitTimeoutRef.current);
      };
    }

    if (rolesWaitTimeoutRef.current) {
      clearTimeout(rolesWaitTimeoutRef.current);
      rolesWaitTimeoutRef.current = null;
    }

    if (hasRedirected.current) return;
    hasRedirected.current = true;

    if (!user) {
      hasRedirected.current = false;
      return;
    }

    // If already on pending page, don't redirect away
    if (window.location.pathname === '/auth/pending') {
      hasRedirected.current = false;
      return;
    }

    // Check account_status: pending_review users go to /auth/pending (async)
    (async () => {
      const { data: profile } = await supabase
        .from('profiles')
        .select('account_status')
        .eq('id', user.id)
        .maybeSingle();
      if (profile?.account_status === 'pending_review') {
        navigate('/auth/pending', { replace: true });
        return;
      }

    // CRITICAL FIX: Check if user is already on a portal route or has a redirect parameter
    // Don't redirect if they're trying to access a specific portal
    const currentPath = window.location.pathname;
    const redirectParam = searchParams.get('redirect');
    const isOnBuyerRoute = currentPath.startsWith('/buyer/');
    const isOnSupplierRoute = currentPath.startsWith('/supplier/');
    const isOnAdminRoute = currentPath.startsWith('/admin/');
    
    // If user is on a portal route, don't redirect - let ProtectedRoute handle it
    // This is critical for multi-role users who want to access a specific portal
    if (isOnBuyerRoute || isOnSupplierRoute || isOnAdminRoute) {
      logger.info('Auth redirect: User already on portal route, skipping redirect', {
        currentPath,
        userId: user.id,
        roles: roles.join(','),
        isOnBuyerRoute,
        isOnSupplierRoute,
        isOnAdminRoute
      });
      hasRedirected.current = false; // Reset so they can navigate freely
      return;
    }
    
    // If there's a redirect parameter, use it (but verify user has access)
    if (redirectParam) {
      const redirectPath = redirectParam.startsWith('/') ? redirectParam : `/${redirectParam}`;
      const isBuyerRedirect = redirectPath.startsWith('/buyer/');
      const isSupplierRedirect = redirectPath.startsWith('/supplier/');
      const isAdminRedirect = redirectPath.startsWith('/admin/');
      
      // Verify user has access to the redirect destination
      if (isBuyerRedirect && hasRole(USER_ROLES.BUYER)) {
        logger.info('Auth redirect: Using redirect parameter (buyer)', { redirectPath, userId: user.id });
        navigate(redirectPath, { replace: true });
        return;
      } else if (isSupplierRedirect && hasRole(USER_ROLES.SUPPLIER)) {
        logger.info('Auth redirect: Using redirect parameter (supplier)', { redirectPath, userId: user.id });
        navigate(redirectPath, { replace: true });
        return;
      } else if (isAdminRedirect && hasRole(USER_ROLES.ADMIN)) {
        logger.info('Auth redirect: Using redirect parameter (admin)', { redirectPath, userId: user.id });
        navigate(redirectPath, { replace: true });
        return;
      }
      // If redirect param doesn't match user's roles, fall through to default logic
    }

    let redirectPath = "/auth";
    const isNewSignup = localStorage.getItem('just_signed_up') === 'true';

    logger.info('Auth redirect: Determining redirect path', {
      userId: user.id,
      roles,
      hasAdmin: hasRole(USER_ROLES.ADMIN),
      hasSupplier: hasRole(USER_ROLES.SUPPLIER),
      hasBuyer: hasRole(USER_ROLES.BUYER),
      hasSupport: hasRole(USER_ROLES.SUPPORT),
      isNewSignup,
      rolesCount: roles.length
    });

    if (hasRole(USER_ROLES.ADMIN)) {
      redirectPath = getDefaultRoute(USER_ROLES.ADMIN);
    } else if (hasRole(USER_ROLES.SUPPLIER)) {
      redirectPath = isNewSignup
        ? `${getProfileRoute(USER_ROLES.SUPPLIER)}?onboarding=true`
        : getDefaultRoute(USER_ROLES.SUPPLIER);
    } else if (hasRole(USER_ROLES.BUYER)) {
      redirectPath = isNewSignup
        ? `${getProfileRoute(USER_ROLES.BUYER)}?onboarding=true`
        : getDefaultRoute(USER_ROLES.BUYER);
    } else if (hasRole(USER_ROLES.SUPPORT)) {
      redirectPath = getDefaultRoute(USER_ROLES.SUPPORT);
    } else if (roles.length === 0) {
      logger.warn('User has no roles assigned, redirecting to auth', { userId: user.id });
      redirectPath = "/auth";
    }

    if (isNewSignup) {
      localStorage.removeItem('just_signed_up');
    }

    logger.info('Auth redirect: Redirecting user', {
      userId: user.id,
      redirectPath,
      isNewSignup: isNewSignup
    });

    navigate(redirectPath, { replace: true });
    })();
  }, [user, authLoading, roles, hasRole, navigate, searchParams]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();

    const emailValidation = emailSchema.safeParse(email);
    if (!emailValidation.success) {
      setEmailError(emailValidation.error.errors[0].message);
      toast.error("Invalid Email", {
        description: emailValidation.error.errors[0].message
      });
      return;
    }
    setEmailError("");

    if (!password || password.length < 6) {
      setPasswordError("Password must be at least 6 characters");
      toast.error("Invalid Password", {
        description: "Password must be at least 6 characters"
      });
      return;
    }
    setPasswordError("");

    setLoading(true);

    try {
      const normalizedEmail = email.toLowerCase().trim();

      const { error, data } = await supabase.auth.signInWithPassword({
        email: normalizedEmail,
        password,
      });

      if (error) {
        const friendlyError = getFriendlyError(error);
        toast.error(friendlyError.title, {
          description: friendlyError.description,
        });
        setLoading(false);
        return;
      }

      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('account_status, locked_at')
        .eq('id', data.user.id)
        .maybeSingle();

      if (profileError) {
        logger.error('Error checking account status', { error: profileError });
      }

      if (profile) {
        if (profile.locked_at) {
          toast.error("Account Locked", {
            description: "Your account has been locked. Please contact support.",
          });
          setLoading(false);
          await supabase.auth.signOut();
          return;
        }

        if (profile.account_status === 'pending_review') {
          setLoading(false);
          navigate('/auth/pending', { replace: true });
          return;
        }
        if (profile.account_status !== 'active') {
          toast.error("Account Inactive", {
            description: `Your account status is: ${profile.account_status}. Please contact support.`,
          });
          setLoading(false);
          await supabase.auth.signOut();
          return;
        }
      }

      toast.success("Login successful!", {
        description: "Redirecting to your dashboard..."
      });

      await new Promise(resolve => setTimeout(resolve, 300));

      const { data: { session: verifySession } } = await supabase.auth.getSession();
      if (!verifySession) {
        logger.error('Session not available after sign-in', { userId: data.user.id });
        toast.error("Session error", {
          description: "Please try signing in again."
        });
        setLoading(false);
        return;
      }

      let rolesLoaded = false;
      let attempts = 0;
      const maxAttempts = 20;

      while (!rolesLoaded && attempts < maxAttempts) {
        const { data: rolesData } = await supabase
          .from('user_roles')
          .select('role')
          .eq('user_id', data.user.id);

        if (rolesData && rolesData.length > 0) {
          rolesLoaded = true;
          logger.info('Roles loaded after sign-in', { userId: data.user.id, roles: rolesData.map(r => r.role) });
          
          // Track login event
          const userRole = rolesData[0]?.role || 'unknown';
          trackLogin('email', userRole);
        } else {
          attempts++;
          await new Promise(resolve => setTimeout(resolve, 200));
        }
      }

      // Reset redirect flag to allow redirect useEffect to run
      hasRedirected.current = false;
      
      // Force a small delay to ensure auth state has propagated
      await new Promise(resolve => setTimeout(resolve, 300));
      
      // Verify session is still valid before redirecting
      const { data: { session: finalSession } } = await supabase.auth.getSession();
      if (!finalSession) {
        logger.error('Session lost after login - refresh token may be invalid');
        toast.error("Session error", {
          description: "Please try signing in again."
        });
        setLoading(false);
        return;
      }

      setLoading(false);
      
      // Force redirect after login - don't rely solely on useEffect
      // Get roles one more time to determine redirect
      const { data: finalRolesData } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', data.user.id);
      
      const finalRoles = finalRolesData?.map(r => r.role) || [];
      let redirectPath = "/auth";
      
      if (finalRoles.includes('admin')) {
        redirectPath = "/admin/dashboard";
      } else if (finalRoles.includes('supplier')) {
        redirectPath = "/supplier/dashboard";
      } else if (finalRoles.includes('buyer')) {
        redirectPath = "/buyer/dashboard";
      } else if (finalRoles.includes('support')) {
        redirectPath = "/admin/dashboard"; // Support uses admin dashboard
      }
      
      logger.info('Login complete, redirecting', { 
        userId: data.user.id, 
        roles: finalRoles,
        redirectPath 
      });
      
      // Small delay to ensure UI updates, then navigate
      setTimeout(() => {
        navigate(redirectPath, { replace: true });
      }, 100);

    } catch (error: unknown) {
      logger.error('Login error', { error });
      toast.error("Login failed", {
        description: "An unexpected error occurred. Please try again."
      });
      setLoading(false);
    }
  };

  const nameSchema = z.string()
    .min(2, "At least 2 characters")
    .regex(/^[a-zA-Z\s'-]+$/, "Letters, spaces, hyphens, apostrophes only");

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();

    const firstNameValidation = nameSchema.safeParse(firstName.trim());
    if (!firstNameValidation.success) {
      toast.error("First name required", { description: firstNameValidation.error.errors[0].message });
      return;
    }
    const lastNameValidation = nameSchema.safeParse(lastName.trim());
    if (!lastNameValidation.success) {
      toast.error("Last name required", { description: lastNameValidation.error.errors[0].message });
      return;
    }

    const emailValidation = emailSchema.safeParse(email);
    if (!emailValidation.success) {
      setEmailError(emailValidation.error.errors[0].message);
      return;
    }
    setEmailError("");

    const passwordValidation = passwordSchema.safeParse(password);
    if (!passwordValidation.success) {
      setPasswordError(passwordValidation.error.errors[0].message);
      toast.error("Password requirements not met", {
        description: passwordValidation.error.errors[0].message
      });
      return;
    }
    setPasswordError("");

    setLoading(true);

    const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
    const signupSourceForTracking = hostname === 'app.holescale.com' ? 'app.holescale.com' : hostname.includes('holescale.com') ? 'holescale.com' : 'app';
    trackSignupAttempt(signupSourceForTracking, role);

    const fullName = `${firstName.trim()} ${lastName.trim()}`.trim();
    const urlParams = new URLSearchParams(window.location.search);
    const signupSource = typeof window !== 'undefined' && window.location.hostname.includes('holescale.com')
      ? (window.location.hostname === 'app.holescale.com' ? 'app.holescale.com' : 'holescale.com')
      : (window.location.origin || 'app');

    const { error, data: signupData } = await supabase.auth.signUp({
      email: email.toLowerCase().trim(),
      password,
      options: {
        emailRedirectTo: `${window.location.origin}/auth`,
        data: {
          full_name: fullName,
          first_name: firstName.trim(),
          last_name: lastName.trim(),
          phone: phone,
          role: role,
          signup_source: signupSource,
          utm_source: urlParams.get('utm_source') || null,
          utm_medium: urlParams.get('utm_medium') || null,
          utm_campaign: urlParams.get('utm_campaign') || null,
        },
      },
    });

    if (error) {
      const friendlyError = getFriendlyError(error);
      logger.error('Signup error', { error, friendlyError });

      const errorTitle = friendlyError.title || 'Signup Failed';
      const errorDescription = friendlyError.description || error.message || 'Please check your information and try again.';

      toast.error(errorTitle, {
        description: errorDescription,
        duration: 5000,
      });

      setLoading(false);
      return;
    }

    if (signupData?.user && !signupData.session) {
      toast.success("Account created!", {
        description: "Please check your email to verify your account before logging in.",
      });
      setMode("login");
      setLoading(false);
      return;
    }

    if (signupData?.session) {
      toast.success("Account created!", {
        description: "Redirecting to your dashboard..."
      });

      // Track signup event
      trackSignUp('email', role, signupSource);
      
      localStorage.setItem('just_signed_up', 'true');
      hasRedirected.current = false;
    } else {
      const { error: loginError } = await supabase.auth.signInWithPassword({
        email: email.toLowerCase().trim(),
        password,
      });

      if (!loginError) {
        hasRedirected.current = false;
      } else {
        toast.info("Account created", {
          description: "Please log in to continue."
        });
        setMode("login");
      }
    }
    setLoading(false);
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();

    const emailValidation = emailSchema.safeParse(email);
    if (!emailValidation.success) {
      setEmailError(emailValidation.error.errors[0].message);
      return;
    }
    setEmailError("");

    setLoading(true);

    // #region agent log: reset password request
    fetch('http://127.0.0.1:7242/ingest/6f266181-65e3-4a94-aa93-5189448d395b', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        id: `log_${Date.now()}_auth_reset_request`,
        runId: 'pre-fix',
        hypothesisId: 'H2',
        location: 'src/pages/Auth.tsx:handleResetPassword',
        message: 'Sending resetPasswordForEmail',
        data: {
          origin: window.location.origin,
          emailDomain: email.split('@')[1] || null,
        },
        timestamp: Date.now(),
      }),
    }).catch(() => {});
    // #endregion

    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });

    if (error) {
      toast.error("Password reset failed", {
        description: error.message
      });
    } else {
      toast.success("Check your email", {
        description: "We've sent you a secure link to reset your password."
      });
    }
    setLoading(false);
  };

  const [showInitialLoading, setShowInitialLoading] = useState(true);

  useEffect(() => {
    // OPTIMIZED: Only wait for session check, not roles, when there's no user
    // This makes the auth page load much faster for unauthenticated users
    // CRITICAL FIX: If there's no user, don't wait for authLoading at all
    // This prevents the page from being stuck when session query hangs
    if (!user) {
      // For unauthenticated users, show form immediately after a brief delay
      // This prevents flash of loading state
      const timer = setTimeout(() => {
        setShowInitialLoading(false);
      }, 100);
      return () => clearTimeout(timer);
    }
    
    // For authenticated users, wait for authLoading to complete
    if (!authLoading) {
      const timer = setTimeout(() => setShowInitialLoading(false), 50);
      return () => clearTimeout(timer);
    }

    // Safety timeout - force display after 2s even if loading is stuck
    // This ensures the page always renders, even if there's a network issue
    const safetyTimer = setTimeout(() => {
      logger.warn('Auth loading timed out, forcing content display', { 
        authLoading, 
        hasUser: !!user 
      });
      setShowInitialLoading(false);
    }, 2000);

    return () => clearTimeout(safetyTimer);
  }, [authLoading, user]);

  // CRITICAL FIX: Additional safety timeout to ensure page always renders
  // This prevents the page from being completely blank if all other checks fail
  useEffect(() => {
    const absoluteSafetyTimer = setTimeout(() => {
      if (showInitialLoading) {
        logger.error('Auth page stuck in loading state, forcing render', {
          authLoading,
          hasUser: !!user,
          showInitialLoading
        });
        setShowInitialLoading(false);
      }
    }, 3000);

    return () => clearTimeout(absoluteSafetyTimer);
  }, [showInitialLoading, authLoading, user]);

  if (authLoading && showInitialLoading && user) {
    // Only show loading spinner if user exists (waiting for roles)
    // For unauthenticated users, show the form immediately
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="text-center">
          <Loader2 className="mx-auto mb-2 size-8 animate-spin text-primary" />
          <p className="text-sm text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen">
      {/* Left Panel - Marketing/Branding */}
      <div className="relative hidden flex-col justify-between overflow-hidden bg-gradient-to-br from-primary via-primary/90 to-primary/80 p-12 text-white lg:flex lg:w-1/2">
        {/* Background pattern */}
        <div className="bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmZmZmYiIGZpbGwtb3BhY2l0eT0iMC4wNSI+PHBhdGggZD0iTTM2IDM0djJoLTJ2LTJoMnptMC00aDJ2Mmgt MnYtMnptLTQgNGgydjJoLTJ2LTJ6bTAtNHYyaC0ydi0yaDJ6bS00IDBoMnYyaC0ydi0yem0wIDRoMnYyaC0ydi0yeiIvPjwvZz48L2c+PC9zdmc+')] absolute inset-0 opacity-50"></div>

        {/* Logo */}
        <div className="relative z-10">
          <a href={MARKETING_URL} className="inline-block">
            <Logo variant="light" width={180} />
          </a>
        </div>

        {/* Value Props */}
        <div className="relative z-10 my-auto space-y-8">
          <div>
            <h1 className="mb-4 text-4xl font-bold">
              B2B Packaging Marketplace
            </h1>
            <p className="text-xl text-white/80">
              Connecting buyers and suppliers in the packaging industry
            </p>
          </div>

          <div className="space-y-6">
            {valueProps.map((prop, index) => (
              <div key={index} className="flex items-start gap-4">
                <div className="flex size-12 shrink-0 items-center justify-center rounded-lg bg-white/10">
                  <prop.icon className="size-6" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold">{prop.title}</h3>
                  <p className="text-sm text-white/70">{prop.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="relative z-10 flex items-center gap-6 text-sm text-white/60">
          <a href={`${MARKETING_URL}/about`} className="transition-colors hover:text-white">About</a>
          <a href={`${MARKETING_URL}/pricing`} className="transition-colors hover:text-white">Pricing</a>
          <a href={`${MARKETING_URL}/contact`} className="transition-colors hover:text-white">Contact</a>
          <a href={`${MARKETING_URL}/blog`} className="transition-colors hover:text-white">Blog</a>
        </div>
      </div>

      {/* Right Panel - Auth Form */}
      <div className="flex w-full items-center justify-center bg-gradient-to-br from-background via-background to-muted/30 p-6 sm:p-8 lg:w-1/2">
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <div className="mb-8 text-center lg:hidden">
            <a href={MARKETING_URL}>
              <Logo width={160} className="mx-auto" />
            </a>
          </div>

          {/* Auth Card */}
          <main className="rounded-xl border border-border bg-card p-6 shadow-lg sm:p-8">
            {/* Header */}
            <header className="mb-6">
              <h2 className="text-center text-2xl font-bold">
                {mode === "login" && "Welcome back"}
                {mode === "signup" && "Create your account"}
                {mode === "reset" && "Reset password"}
              </h2>
              <p className="mt-1 text-center text-sm text-muted-foreground">
                {mode === "login" && "Sign in to access your dashboard"}
                {mode === "signup" && "Get started with HoleScale"}
                {mode === "reset" && "We'll send you a reset link"}
              </p>
            </header>

            {/* Login Form */}
            {mode === "login" && (
              <form onSubmit={handleLogin} className="space-y-4" aria-label="Login form">
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    placeholder="your@email.com"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      setEmailError("");
                    }}
                    onBlur={() => {
                      const validation = emailSchema.safeParse(email);
                      if (email && !validation.success) {
                        setEmailError(validation.error.errors[0].message);
                      }
                    }}
                    className={emailError ? "border-destructive focus-visible:ring-destructive" : ""}
                    required
                    autoFocus
                    autoComplete="email"
                    aria-required="true"
                    aria-invalid={!!emailError}
                    aria-describedby={emailError ? "email-error" : undefined}
                  />
                  {emailError && (
                    <p id="email-error" className="text-xs text-destructive" role="alert">{emailError}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="password">Password</Label>
                    <button
                      type="button"
                      onClick={() => setMode("reset")}
                      className="text-xs text-muted-foreground hover:text-foreground hover:underline"
                    >
                      Forgot password?
                    </button>
                  </div>
                  <div className="relative">
                    <Input
                      id="password"
                      name="password"
                      type={showPassword ? "text" : "password"}
                      placeholder="Enter your password"
                      value={password}
                      onChange={(e) => {
                        setPassword(e.target.value);
                        setPasswordError("");
                      }}
                      className={`pr-10 ${passwordError ? "border-destructive focus-visible:ring-destructive" : ""}`}
                      required
                      autoComplete="current-password"
                      aria-required="true"
                      aria-invalid={!!passwordError}
                      aria-describedby={passwordError ? "password-error" : undefined}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      tabIndex={-1}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground transition-colors hover:text-foreground"
                      aria-label={showPassword ? "Hide password" : "Show password"}
                    >
                      {showPassword ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
                    </button>
                  </div>
                  {passwordError && (
                    <p id="password-error" className="text-xs text-destructive" role="alert">{passwordError}</p>
                  )}
                </div>

                <Button type="submit" className="w-full" size="lg" disabled={loading}>
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 size-4 animate-spin" />
                      Signing in...
                    </>
                  ) : (
                    <>
                      Sign in
                      <ArrowRight className="ml-2 size-4" />
                    </>
                  )}
                </Button>

                <div className="relative my-4 flex items-center">
                  <Separator className="flex-1" />
                  <span className="whitespace-nowrap px-3 text-xs text-muted-foreground">
                    or continue with
                  </span>
                  <Separator className="flex-1" />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <GoogleOAuthButton />
                  <AppleOAuthButton />
                </div>

                <div className="pt-4 text-center">
                  {!SIGNUPS_ENABLED ? (
                    <p className="text-sm text-muted-foreground">
                      New here?{" "}
                      <a href={`${MARKETING_URL}/waitlist`} className="font-semibold text-primary hover:underline">
                        Join our waitlist
                      </a>
                    </p>
                  ) : (
                    <div className="flex flex-col items-center justify-center gap-2 sm:flex-row sm:gap-4">
                      <p className="text-sm text-muted-foreground">Don't have an account?</p>
                      <Button
                        type="button"
                        variant="default"
                        size="sm"
                        onClick={() => setMode("signup")}
                        className="font-semibold"
                      >
                        Sign up free
                      </Button>
                    </div>
                  )}
                </div>
              </form>
            )}

            {/* Signup disabled notice */}
            {mode === "signup" && !SIGNUPS_ENABLED && !searchParams.get("invite") && (
              <div className="space-y-4">
                <div className="rounded-lg border border-info/20 bg-info/5 p-6 text-center dark:border-blue-800 dark:bg-info/20">
                  <div className="flex flex-col items-center gap-4">
                    <Info className="size-8 text-info dark:text-info" />
                    <div>
                      <h3 className="mb-2 text-lg font-semibold text-blue-900 dark:text-info-foreground">
                        Currently in Early Access
                      </h3>
                      <p className="mb-4 text-sm text-info dark:text-info">
                        We're launching soon! Join our waitlist to get notified.
                      </p>
                      <a href={`${MARKETING_URL}/waitlist`}>
                        <Button className="gap-2">
                          Join the Waitlist
                          <ArrowRight className="size-4" />
                        </Button>
                      </a>
                    </div>
                  </div>
                </div>
                <div className="text-center">
                  <button
                    type="button"
                    onClick={() => setMode("login")}
                    className="text-sm text-muted-foreground hover:text-foreground"
                  >
                    Already have an account? <span className="font-semibold">Sign in</span>
                  </button>
                </div>
              </div>
            )}

            {/* Signup Form */}
            {mode === "signup" && SIGNUPS_ENABLED && (
              <form onSubmit={handleSignup} className="space-y-4" aria-label="Sign up form">
                {/* Role selector first - required for OAuth flow */}
                <fieldset className="space-y-2">
                  <legend className="text-sm font-medium">I am a</legend>
                  <div className="grid grid-cols-2 gap-4" role="radiogroup" aria-required="true">
                    <button
                      type="button"
                      role="radio"
                      aria-checked={role === USER_ROLES.BUYER}
                      onClick={() => setRole(USER_ROLES.BUYER as "buyer" | "supplier")}
                      className={`rounded-lg border-2 px-4 py-3 transition-all ${role === USER_ROLES.BUYER
                          ? "border-primary bg-primary/10 text-primary"
                          : "border-muted hover:border-primary/50"
                        }`}
                    >
                      <div className="flex flex-col items-center gap-1">
                        <ShoppingCart className="size-5" />
                        <span className="text-sm font-semibold">Buyer</span>
                      </div>
                    </button>
                    <button
                      type="button"
                      role="radio"
                      aria-checked={role === USER_ROLES.SUPPLIER}
                      onClick={() => setRole(USER_ROLES.SUPPLIER as "buyer" | "supplier")}
                      className={`rounded-lg border-2 px-4 py-3 transition-all ${role === USER_ROLES.SUPPLIER
                          ? "border-primary bg-primary/10 text-primary"
                          : "border-muted hover:border-primary/50"
                        }`}
                    >
                      <div className="flex flex-col items-center gap-1">
                        <Package className="size-5" />
                        <span className="text-sm font-semibold">Supplier</span>
                      </div>
                    </button>
                  </div>
                </fieldset>

                {/* OAuth first - fastest signup */}
                <div className="space-y-4">
                  <p className="text-center text-sm text-muted-foreground">
                    Continue with Google or Apple — fastest way to get started
                  </p>
                  <div className="grid grid-cols-2 gap-4">
                    <GoogleOAuthButton role={role} />
                    <AppleOAuthButton role={role} />
                  </div>
                </div>

                <div className="relative my-4 flex items-center">
                  <Separator className="flex-1" />
                  <span className="whitespace-nowrap px-3 text-xs text-muted-foreground">
                    or continue with email
                  </span>
                  <Separator className="flex-1" />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="firstName">First Name</Label>
                    <Input
                      id="firstName"
                      name="firstName"
                      type="text"
                      placeholder="John"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      required
                      aria-required="true"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="lastName">Last Name</Label>
                    <Input
                      id="lastName"
                      name="lastName"
                      type="text"
                      placeholder="Doe"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      required
                      aria-required="true"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="signup-email">Email</Label>
                  <Input
                    id="signup-email"
                    name="email"
                    type="email"
                    placeholder="your@email.com"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      setEmailError("");
                    }}
                    className={emailError ? "border-destructive focus-visible:ring-destructive" : ""}
                    required
                    aria-required="true"
                    aria-invalid={!!emailError}
                    aria-describedby={emailError ? "signup-email-error" : undefined}
                  />
                  {emailError && (
                    <p id="signup-email-error" className="text-xs text-destructive" role="alert">{emailError}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="signup-password">Password</Label>
                  <div className="relative">
                    <Input
                      id="signup-password"
                      name="password"
                      type={showPassword ? "text" : "password"}
                      placeholder="Create a password"
                      value={password}
                      onChange={(e) => {
                        setPassword(e.target.value);
                        setPasswordError("");
                      }}
                      className={passwordError ? "border-destructive pr-10" : "pr-10"}
                      required
                      aria-required="true"
                      aria-invalid={!!passwordError}
                      aria-describedby={passwordError ? "signup-password-error password-strength" : "password-strength"}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      aria-label={showPassword ? "Hide password" : "Show password"}
                    >
                      {showPassword ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
                    </button>
                  </div>
                  {passwordError && (
                    <p id="signup-password-error" className="text-xs text-destructive" role="alert">{passwordError}</p>
                  )}
                  <div id="password-strength">
                    <PasswordStrengthIndicator password={password} show={password.length > 0} />
                  </div>
                </div>

                <Button type="submit" className="w-full" size="lg" disabled={loading}>
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 size-4 animate-spin" />
                      Creating account...
                    </>
                  ) : (
                    <>
                      Create account
                      <ArrowRight className="ml-2 size-4" />
                    </>
                  )}
                </Button>

                <div className="pt-2 text-center">
                  <p className="text-sm text-muted-foreground">
                    Already have an account?{" "}
                    <button
                      type="button"
                      onClick={() => setMode("login")}
                      className="font-semibold text-primary hover:underline"
                    >
                      Sign in
                    </button>
                  </p>
                </div>
              </form>
            )}

            {/* Reset Password Form */}
            {mode === "reset" && (
              <form onSubmit={handleResetPassword} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="reset-email">Email</Label>
                  <Input
                    id="reset-email"
                    type="email"
                    placeholder="your@email.com"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      setEmailError("");
                    }}
                    className={emailError ? "border-destructive" : ""}
                    required
                  />
                  {emailError && (
                    <p className="text-xs text-destructive" role="alert">{emailError}</p>
                  )}
                </div>

                <Button type="submit" className="w-full" size="lg" disabled={loading}>
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 size-4 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      Send reset link
                      <ArrowRight className="ml-2 size-4" />
                    </>
                  )}
                </Button>

                <Button
                  type="button"
                  variant="ghost"
                  className="w-full"
                  onClick={() => setMode("login")}
                >
                  Back to sign in
                </Button>
              </form>
            )}

            {/* Footer */}
            <div className="mt-6 space-y-2 text-center text-xs text-muted-foreground">
              <p className="flex items-center justify-center gap-1.5">
                <Shield className="size-3.5 text-success" />
                Your data is secure with us
              </p>
              <p>
                By continuing, you agree to our{" "}
                <a href={`${MARKETING_URL}/terms`} className="text-foreground hover:underline">Terms</a>
                {" "}and{" "}
                <a href={`${MARKETING_URL}/privacy`} className="text-foreground hover:underline">Privacy Policy</a>
              </p>
            </div>
          </main>

          {/* Mobile footer links */}
          <div className="mt-6 flex items-center justify-center gap-4 text-sm text-muted-foreground lg:hidden">
            <a href={`${MARKETING_URL}/about`} className="hover:text-foreground">About</a>
            <a href={`${MARKETING_URL}/pricing`} className="hover:text-foreground">Pricing</a>
            <a href={`${MARKETING_URL}/contact`} className="hover:text-foreground">Contact</a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Auth;
